import { Subject } from 'rxjs'

export interface Permission {
  id: string
  name: string
  require?: string[]
}

export interface PermissionGroup {
  name: string
  icon?: string
  component?: string
  items: Permission[]
}

export interface PermissionSchema {
  permissionGroups: PermissionGroup[]
}

export class PermissionModel {
  value: string[] = []
  valueChange$ = new Subject<string[]>()
  blacklist: Permission[] = []

  constructor (public schema: PermissionSchema) { }

  setValue (value: string[]): void {
    this.updateValue(value)
  }

  getPermission (id: string): Permission|null {
    return this.getAllPermissions().find(x => x.id === id) ?? null
  }

  getAllPermissions (): Permission[] {
    let result: Permission[] = []
    for (const g of this.schema.permissionGroups) {
      result = result.concat(g.items)
    }
    return result
  }

  addPermission (permission: Permission): void {
    if (this.blacklist.includes(permission)) {
      return
    }
    if (!this.value.includes(permission.id)) {
      for (const dep of permission.require ?? []) {
        if (!this.value.includes(dep)) {
          if (this.blacklist.some(x => x.id === dep)) {
            return
          }
          this.addPermission(this.getPermission(dep)!)
        }
      }
      this.updateValue([...this.value, permission.id])
    }
  }

  togglePermission (permission: Permission): void {
    if (this.value.includes(permission.id)) {
      this.removePermission(permission)
    } else {
      this.addPermission(permission)
    }
  }

  removePermission (permission: Permission): void {
    for (const p of this.getAllPermissions()) {
      if (this.value.includes(p.id) && p.require?.includes(permission.id)) {
        this.removePermission(p)
      }
    }
    if (this.value.includes(permission.id)) {
      this.updateValue(this.value.filter(x => x !== permission.id))
    }
  }

  isPermissionDependenciesSatisfied (permission: Permission): boolean {
    return !this.schema.permissionGroups
      .some(g => g.items.some(other =>
        permission.require?.includes(other.id) && !this.value.includes(other.id),
      ))
  }

  private updateValue (value: string[]) {
    this.value = value
    this.valueChange$.next(value)
  }
}
